import { HistoryOutlined, Search } from '@mui/icons-material';
import type { CustomPanelControls } from '@practium/ui';
import { Button, FilterChip, Pagination, PeriodPanel } from '@practium/ui';
import { useCallback } from 'react';
import { useHistoryFilter } from '../../hooks/useHistoryFilter';
import styles from './HistoryTable.module.css';
import { formatDateTime } from '@practium/features/parcelling';

export const HistoryTable = () => {
	const {
		page,
		setPage,
		totalPages,
		isLoading,
		isError,
		refetch,
		searchQuery,
		setSearchQuery,
		filterValues,
		setFilterValues,
		filterGroups,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		filteredItems,
	} = useHistoryFilter();

	const renderPeriodPanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<PeriodPanel
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onChange={(from, to) => {
					setCustomDateFrom(from);
					setCustomDateTo(to);
					activate();
				}}
				onClose={close}
			/>
		),
		[customDateFrom, customDateTo, setCustomDateFrom, setCustomDateTo]
	);

	const isEmpty = filteredItems.length === 0;

	return (
		<div className={styles.historyTable}>
			<div className={styles.header}>
				<h3 className={styles.title}>Histórico</h3>
				<div className={styles.controls}>
					<FilterChip
						values={filterValues}
						onChange={setFilterValues}
						groups={filterGroups}
						placeholder="Filtros"
						customPanels={{ 'periodo:custom': renderPeriodPanel }}
					/>
					<div className={styles.searchWrapper}>
						<Search className={styles.searchIcon} />
						<input
							className={styles.searchInput}
							type="text"
							placeholder="Buscar..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</div>
				</div>
			</div>

			{isLoading ? (
				<div className={styles.emptyState} role="status">
					<span className={styles.emptyTitle}>Carregando histórico...</span>
				</div>
			) : isError ? (
				<div className={styles.emptyState} role="alert">
					<span className={styles.emptyTitle}>Não foi possível carregar o histórico</span>
					<Button onClick={() => refetch()}>Tentar novamente</Button>
				</div>
			) : isEmpty ? (
				<div className={styles.emptyState}>
					<HistoryOutlined className={styles.emptyIcon} />
					<span className={styles.emptyTitle}>
						{'Nenhuma atividade registrada'}
					</span>
					<span className={styles.emptyDescription}>
						{'Tente ajustar os filtros ou a busca'}
					</span>
				</div>
			) : (
				<div className={styles.tableContainer}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Categoria</th>
								<th>Ação</th>
								<th>Descrição</th>
								<th>Data</th>
							</tr>
						</thead>
						<tbody>
							{filteredItems.map((item) => (
								<tr key={item._id}>
									<td>{item.category}</td>
									<td>{item.action}</td>
									<td>{item.description}</td>
									<td>{formatDateTime(item.createdAt)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<div className={styles.paginationRow}>
				<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
			</div>
		</div>
	);
};
